import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaPlus, FaEllipsisV, FaFilter, FaChevronDown, FaChevronUp, FaCalendarAlt } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';
import ReactDOM from 'react-dom';

const ROWS_PER_PAGE = 25;

// Portal dropdown component (unchanged)
const DropdownPortal = ({ anchorRef, open, onClose, children }) => {
  const menuRef = useRef();
  const [style, setStyle] = useState({});

  useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        zIndex: 9999,
        minWidth: 112,
      });
    }
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return ReactDOM.createPortal(
    <div ref={menuRef} style={style} className="w-28 bg-white border border-gray-200 rounded shadow-lg">
      {children}
    </div>,
    document.body
  );
};

const ReceivedPaymentList = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [userRole, setUserRole] = useState('');
  const menuButtonRefs = useRef({});

  const [showDateFilters, setShowDateFilters] = useState(false);
  const [filters, setFilters] = useState({
    contractDateFrom: '',
    contractDateTo: '',
    buyerName: '',
    status: ''
  });

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/profile/profile-get`,
          { withCredentials: true }
        );
        if (response.data.success) {
          setUserRole(response.data.data.role);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };
    fetchUserRole();
  }, []);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      contractDateFrom: '',
      contractDateTo: '',
      buyerName: '',
      status: ''
    });
    setPage(1);
  };

  const toggleDateFilters = () => {
    setShowDateFilters(!showDateFilters);
  };

  const clearAllFilters = () => {
    setSearch('');
    setFilters({
      contractDateFrom: '',
      contractDateTo: '',
      buyerName: '',
      status: ''
    });
    setShowDateFilters(false);
    setPage(1);
  };

  const fetchRecords = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });

      if (filters.contractDateFrom) {
        params.append('contractDateFrom', filters.contractDateFrom);
      }
      if (filters.contractDateTo) {
        params.append('contractDateTo', filters.contractDateTo);
      }
      if (filters.buyerName) {
        params.append('buyerName', filters.buyerName);
      }
      if (filters.status && filters.status.trim() !== '') {
        params.append('status', filters.status);
      }

      const res = await fetch(`${API_BASE_URL}/api/masters/received-payment-get?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setRecords(result.data || []);
      setTotal(result.total || 0);
      setGrandTotal(result.grandTotal || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch received payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch, filters]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0.00';
    return parseFloat(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleStatusChange = async (received_id, newStatus) => {
    setActionLoadingId(received_id);
    setActionMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/received-payment-post/${received_id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (res.ok) {
        setRecords(prev => prev.map(r => r.received_id === received_id ? { ...r, status: newStatus, approved_by: data.approved_by } : r));
        setActionMessage({ type: 'success', text: `Status updated to ${newStatus}` });
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to update status' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Server error' });
    }
    setActionLoadingId(null);
    setMenuOpenId(null);
  };

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-lg font-medium text-gray-800">Received Payment List</h2>
        <div className="flex gap-1 w-full sm:w-auto justify-end">
          <input
            type="text"
            placeholder="Search by contract, type, status..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            onClick={toggleDateFilters}
            className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded hover:bg-blue-100 text-xs font-medium transition-colors"
          >
            <FaFilter className="text-blue-600" />
            {showDateFilters ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
            {(filters.contractDateFrom || filters.contractDateTo || filters.buyerName || filters.status) && (
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            )}
          </button>
          <button
            className="flex items-center gap-1 bg-[#2691cc] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#2691cc] font-medium"
            onClick={() => navigate('/received-payment/new')}
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>

      {showDateFilters && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <FaCalendarAlt className="text-blue-600" />
              Sales Contract Date Filter
            </h4>
            {(search || filters.contractDateFrom || filters.contractDateTo || filters.buyerName || filters.status) && (
              <button
                onClick={clearAllFilters}
                className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Clear All Filters
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1">Sales Contract Date From</label>
              <input
                type="date"
                name="contractDateFrom"
                value={filters.contractDateFrom}
                onChange={handleFilterChange}
                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1">Sales Contract Date To</label>
              <input
                type="date"
                name="contractDateTo"
                value={filters.contractDateTo}
                onChange={handleFilterChange}
                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1">Buyer Name</label>
              <input
                type="text"
                name="buyerName"
                value={filters.buyerName}
                onChange={handleFilterChange}
                placeholder="Search by buyer name..."
                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="In Process">In Process</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>

          {(filters.contractDateFrom || filters.contractDateTo || filters.buyerName || filters.status) && (
            <div className="mt-3 flex items-center justify-between bg-white p-2 rounded border border-blue-200">
              <span className="text-xs text-blue-700 font-medium">
                {[
                  filters.contractDateFrom && filters.contractDateTo
                    ? `Sales contract date: ${filters.contractDateFrom} to ${filters.contractDateTo}`
                    : filters.contractDateTo
                    ? `Sales contract date up to ${filters.contractDateTo}`
                    : filters.contractDateFrom
                    ? `Sales contract date from ${filters.contractDateFrom} onwards`
                    : null,
                  filters.buyerName ? `Buyer name: ${filters.buyerName}` : null,
                  filters.status ? `Status: ${filters.status}` : null
                ].filter(Boolean).join(' | ')}
              </span>
              <button
                onClick={resetFilters}
                className="px-2 py-1 text-xs bg-blue-200 text-blue-700 rounded hover:bg-blue-300 font-medium"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : records.length === 0 ? (
        <NoRecordFound message="No received payment records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Sales Contract</th>
                <th className="px-3 py-2 text-left font-medium">Contract Date</th>
                <th className="px-3 py-2 text-left font-medium">Buyer Name</th>
                <th className="px-3 py-2 text-left font-medium">Received Date</th>
                <th className="px-3 py-2 text-left font-medium">Amount</th>
                <th className="px-3 py-2 text-left font-medium">Payment Type</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Remarks</th>
                {/* ✅ CHANGED: "Created At" replaced with "Created By" */}
                <th className="px-3 py-2 text-left font-medium">Created By</th>
                 <th className="px-3 py-2 text-left font-medium">Approved By</th>
                {['admin', 'developer', 'finance', 'account'].includes(userRole?.toLowerCase()) && (
                  <th className="px-3 py-2 text-left font-medium">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {records.map((item, idx) => (
                <tr key={item.received_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-1">{item.sales_contract_no || 'N/A'}</td>
                  <td className="px-3 py-1">{formatDate(item.contract_date)}</td>
                  <td className="px-3 py-1">{item.buyer_name || '-'}</td>
                  <td className="px-3 py-1">{formatDate(item.received_date)}</td>
                  <td className="px-3 py-1">₹{formatCurrency(item.amount_received)}</td>
                  <td className="px-3 py-1">
                    <span className="">
                      {item.payment_type}
                    </span>
                  </td>
                  <td className="px-3 py-1">
                    <span className={`${item.status === 'Rejected' ? ' text-red-700' : ''}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-3 py-1 max-w-xs truncate" title={item.remarks}>
                    {item.remarks || '-'}
                  </td>
                  {/* ✅ CHANGED: Display creator name instead of created_at */}
                  <td className="px-3 py-1">{item.created_by_name || '-'}</td>
                     <td className="px-3 py-1">{item.approved_by || '-'}</td>
                  {['admin', 'developer', 'finance', 'account'].includes(userRole?.toLowerCase()) && (
                    <td className="px-3 py-1 relative overflow-visible">
                      {item.status === 'In Process' && (
                        <>
                          <button
                            ref={el => (menuButtonRefs.current[item.received_id] = el)}
                            className="p-1 rounded hover:bg-gray-200"
                            onClick={() => setMenuOpenId(menuOpenId === item.received_id ? null : item.received_id)}
                            disabled={actionLoadingId === item.received_id}
                          >
                            <FaEllipsisV />
                          </button>
                          <DropdownPortal
                            anchorRef={{ current: menuButtonRefs.current[item.received_id] }}
                            open={menuOpenId === item.received_id}
                            onClose={() => setMenuOpenId(null)}
                          >
                            <button
                              className="block w-full text-left px-3 py-1 text-xs"
                              onClick={() => handleStatusChange(item.received_id, 'Approved')}
                              disabled={actionLoadingId === item.received_id}
                            >
                              {actionLoadingId === item.received_id ? 'Approving...' : 'Approve'}
                            </button>
                            <button
                              className="block w-full text-left px-3 py-1 text-red-700 hover:bg-red-50 text-xs"
                              onClick={() => handleStatusChange(item.received_id, 'Rejected')}
                              disabled={actionLoadingId === item.received_id}
                            >
                              {actionLoadingId === item.received_id ? 'Rejecting...' : 'Reject'}
                            </button>
                          </DropdownPortal>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {/* Grand Total Row */}
              <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                <td colSpan="4" className="px-3 py-2 text-right text-gray-700">
                  Grand Total:
                </td>
                <td className="px-3 py-2 text-gray-700">
                  ₹{formatCurrency(grandTotal)}
                </td>
                <td colSpan={['admin', 'developer', 'finance', 'account'].includes(userRole?.toLowerCase()) ? "6" : "5"} className="px-3 py-2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {actionMessage && (
        <div className={`mb-2 text-center text-sm ${actionMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{actionMessage.text}</div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4 px-2">
          <div className="text-sm text-gray-600">
            Showing {((page - 1) * ROWS_PER_PAGE) + 1} to {Math.min(page * ROWS_PER_PAGE, total)} of {total} records
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivedPaymentList;
